import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// ── Thunks ────────────────────────────────────────────────────────────────────

export const fetchTree = createAsyncThunk(
  'reporting/fetchTree',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/reporting/tree');
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to load reporting tree');
    }
  }
);

export const createFolder = createAsyncThunk(
  'reporting/createFolder',
  async ({ name, parentId }, { rejectWithValue }) => {
    try {
      const res = await api.post('/reporting/folders', { name, parent_id: parentId || null });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to create folder');
    }
  }
);

export const updateFolder = createAsyncThunk(
  'reporting/updateFolder',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/reporting/folders/${id}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update folder');
    }
  }
);

export const deleteFolder = createAsyncThunk(
  'reporting/deleteFolder',
  async ({ id }, { rejectWithValue }) => {
    try {
      await api.delete(`/reporting/folders/${id}`);
      return { id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to delete folder');
    }
  }
);

export const createDocument = createAsyncThunk(
  'reporting/createDocument',
  async ({ name, folderId, chartType, color }, { rejectWithValue }) => {
    try {
      const res = await api.post('/reporting/documents', {
        name,
        folder_id: folderId || null,
        chart_type: chartType || 'gantt',
        color: color || '#2563eb',
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to create document');
    }
  }
);

export const fetchDocument = createAsyncThunk(
  'reporting/fetchDocument',
  async ({ id }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/reporting/documents/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to load document');
    }
  }
);

export const updateDocument = createAsyncThunk(
  'reporting/updateDocument',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/reporting/documents/${id}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update document');
    }
  }
);

export const deleteDocument = createAsyncThunk(
  'reporting/deleteDocument',
  async ({ id }, { rejectWithValue }) => {
    try {
      await api.delete(`/reporting/documents/${id}`);
      return { id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to delete document');
    }
  }
);

export const importFromProject = createAsyncThunk(
  'reporting/importFromProject',
  async ({ docId, projectId, scope }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/reporting/documents/${docId}/import`, {
        project_id: projectId,
        scope,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to import from project');
    }
  }
);

export const createShareLink = createAsyncThunk(
  'reporting/createShareLink',
  async ({ docId }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/reporting/documents/${docId}/share`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to create share link');
    }
  }
);

export const revokeShareLink = createAsyncThunk(
  'reporting/revokeShareLink',
  async ({ docId }, { rejectWithValue }) => {
    try {
      await api.delete(`/reporting/documents/${docId}/share`);
      return { docId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to revoke share link');
    }
  }
);

export const createGroup = createAsyncThunk(
  'reporting/createGroup',
  async ({ docId, data }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/reporting/documents/${docId}/groups`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to create group');
    }
  }
);

export const updateGroup = createAsyncThunk(
  'reporting/updateGroup',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/reporting/groups/${id}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update group');
    }
  }
);

export const deleteGroup = createAsyncThunk(
  'reporting/deleteGroup',
  async ({ id }, { rejectWithValue }) => {
    try {
      await api.delete(`/reporting/groups/${id}`);
      return { id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to delete group');
    }
  }
);

export const createRow = createAsyncThunk(
  'reporting/createRow',
  async ({ groupId, data }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/reporting/groups/${groupId}/rows`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to create row');
    }
  }
);

export const updateRow = createAsyncThunk(
  'reporting/updateRow',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/reporting/rows/${id}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update row');
    }
  }
);

export const deleteRow = createAsyncThunk(
  'reporting/deleteRow',
  async ({ id }, { rejectWithValue }) => {
    try {
      await api.delete(`/reporting/rows/${id}`);
      return { id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to delete row');
    }
  }
);

export const createDependency = createAsyncThunk(
  'reporting/createDependency',
  async ({ docId, fromRowId, toRowId }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/reporting/documents/${docId}/dependencies`, {
        from_row_id: fromRowId,
        to_row_id: toRowId,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to create dependency');
    }
  }
);

export const deleteDependency = createAsyncThunk(
  'reporting/deleteDependency',
  async ({ id }, { rejectWithValue }) => {
    try {
      await api.delete(`/reporting/dependencies/${id}`);
      return { id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to delete dependency');
    }
  }
);

// ── Helpers ───────────────────────────────────────────────────────────────────

const updateFolderInTree = (folders, updatedFolder) =>
  folders.map(f => {
    if (f.id === updatedFolder.id) return { ...f, ...updatedFolder };
    if (f.children?.length) return { ...f, children: updateFolderInTree(f.children, updatedFolder) };
    return f;
  });

// ── Slice ─────────────────────────────────────────────────────────────────────

const initialState = {
  tree: { folders: [], loose_documents: [] },
  currentDoc: null,
  shareInfo: null,
  loading: false,
  saving: false,
  error: null,
};

const reportingSlice = createSlice({
  name: 'reporting',
  initialState,
  reducers: {
    clearCurrentDoc(state) {
      state.currentDoc = null;
    },
    updateGroupLocally(state, action) {
      if (!state.currentDoc) return;
      const { id, changes } = action.payload;
      const idx = state.currentDoc.groups.findIndex(g => g.id === id);
      if (idx !== -1) {
        state.currentDoc.groups[idx] = { ...state.currentDoc.groups[idx], ...changes };
      }
    },
    updateRowLocally(state, action) {
      if (!state.currentDoc) return;
      const { id, groupId, changes } = action.payload;
      const group = state.currentDoc.groups.find(g => g.id === groupId);
      if (!group) return;
      const idx = group.rows.findIndex(r => r.id === id);
      if (idx !== -1) {
        group.rows[idx] = { ...group.rows[idx], ...changes };
      }
    },
  },
  extraReducers: (builder) => {
    // fetchTree
    builder
      .addCase(fetchTree.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTree.fulfilled, (state, action) => {
        state.loading = false;
        state.tree = action.payload;
      })
      .addCase(fetchTree.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // updateFolder → update in tree immediately
    builder.addCase(updateFolder.fulfilled, (state, action) => {
      state.tree.folders = updateFolderInTree(state.tree.folders, action.payload);
    });

    // fetchDocument
    builder
      .addCase(fetchDocument.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchDocument.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDoc = action.payload;
      })
      .addCase(fetchDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // createGroup → append to currentDoc.groups
    builder.addCase(createGroup.fulfilled, (state, action) => {
      if (state.currentDoc && state.currentDoc.id === action.payload.document_id) {
        state.currentDoc.groups.push(action.payload);
      }
    });

    // updateGroup
    builder.addCase(updateGroup.fulfilled, (state, action) => {
      if (!state.currentDoc) return;
      const idx = state.currentDoc.groups.findIndex(g => g.id === action.payload.id);
      if (idx !== -1) state.currentDoc.groups[idx] = action.payload;
    });

    // deleteGroup
    builder.addCase(deleteGroup.fulfilled, (state, action) => {
      if (!state.currentDoc) return;
      state.currentDoc.groups = state.currentDoc.groups.filter(g => g.id !== action.payload.id);
    });

    // createRow → append to correct group
    builder.addCase(createRow.fulfilled, (state, action) => {
      if (!state.currentDoc) return;
      const group = state.currentDoc.groups.find(g => g.id === action.payload.group_id);
      if (group) group.rows.push(action.payload);
    });

    // updateRow
    builder.addCase(updateRow.fulfilled, (state, action) => {
      if (!state.currentDoc) return;
      for (const group of state.currentDoc.groups) {
        const idx = group.rows.findIndex(r => r.id === action.payload.id);
        if (idx !== -1) { group.rows[idx] = action.payload; break; }
      }
    });

    // deleteRow
    builder.addCase(deleteRow.fulfilled, (state, action) => {
      if (!state.currentDoc) return;
      for (const group of state.currentDoc.groups) {
        group.rows = group.rows.filter(r => r.id !== action.payload.id);
      }
    });

    // createDependency
    builder.addCase(createDependency.fulfilled, (state, action) => {
      if (!state.currentDoc) return;
      if (!state.currentDoc.dependencies) state.currentDoc.dependencies = [];
      const exists = state.currentDoc.dependencies.some(d => d.id === action.payload.id);
      if (!exists) state.currentDoc.dependencies.push(action.payload);
    });

    // deleteDependency
    builder.addCase(deleteDependency.fulfilled, (state, action) => {
      if (!state.currentDoc) return;
      state.currentDoc.dependencies = (state.currentDoc.dependencies || []).filter(
        d => d.id !== action.payload.id
      );
    });

    // importFromProject → reload doc groups
    builder.addCase(importFromProject.fulfilled, (state, action) => {
      // The import returns the updated document mini — we need a full refetch
      // Just mark as saved so the component can refetch
      state.saving = false;
    });

    // shareLink
    builder.addCase(createShareLink.fulfilled, (state, action) => {
      state.shareInfo = action.payload;
      if (state.currentDoc) state.currentDoc.share_token = action.payload.share_token;
    });
    builder.addCase(revokeShareLink.fulfilled, (state) => {
      state.shareInfo = null;
      if (state.currentDoc) state.currentDoc.share_token = null;
    });
  },
});

export const { clearCurrentDoc, updateGroupLocally, updateRowLocally } = reportingSlice.actions;
export default reportingSlice.reducer;
